import React, { forwardRef, useEffect, useImperativeHandle, useState, Dispatch, SetStateAction } from 'react';
import { SortableOnDragOverEvent } from '@progress/kendo-react-sortable';
import Sortable from '../sortable';
import FilterListItem, { FilterListDataItem } from '@components-clean/core/filter-list/filter-list-item';
import AddEditFilterListItem from 'views-clean/components/core/filter-list/add-edit-filter-list-item';
import AddNewFilterListItem from 'views-clean/components/core/filter-list/add-new-filter-list-item';
import { useBoolean } from '@hooks/index';

interface FilterListProps<T> {
    data: FilterListDataItem[];
    selectData: T[];
    isEdit: boolean;
    displayField?: keyof T & string;
    valueField?: keyof T & string;
    setData: Dispatch<SetStateAction<FilterListDataItem[]>>;
}

export interface FilterListDataHandle {
    getFiltersData: () => FilterListDataItem[];
}

const modifyFilterListItems = (
    items: FilterListDataItem[],
    onDelete: (fieldKey: string) => void,
    onEdit: (fieldKey: string) => void,
) =>
    items.map((item) => ({
        ...item,
        onDelete,
        onEdit,
    }));

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const FilterList = forwardRef(({ data, isEdit, selectData, displayField, valueField, setData }, ref) => {
    const showAddEditDialog = useBoolean(false);

    const [listItems, setListItems] = useState<FilterListDataItem[]>([]);
    const [currentItem, setCurrentItem] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
        getFiltersData: () => listItems,
    }));

    const onDelete = (fieldKey: string) => {
        setListItems((prevState) => prevState.filter((item) => item.fieldKey !== fieldKey));
        setData((prevState) => prevState.filter((item) => item.fieldKey !== fieldKey));
    };

    const onEdit = (fieldKey: string) => {
        setCurrentItem(fieldKey);
        showAddEditDialog.setTrue();
    };

    const onDialogOpen = () => {
        showAddEditDialog.setTrue();
    };

    const onAddNew = <T,>(formData: { fieldKey: T; labelText: FilterListDataItem['labelText'] }) => {
        const newItems = listItems.map((item) => ({ ...item }));
        const newItem = {
            isEdit: true,
            isDisabled: false,
            isDefault: false,
            id: 0,
            onDelete,
            onEdit,
            fieldKey:
                typeof formData.fieldKey === 'string'
                    ? formData.fieldKey
                    : (formData.fieldKey?.[valueField as unknown as keyof T] as string),
            fieldName:
                typeof formData.fieldKey === 'string'
                    ? formData.fieldKey
                    : (formData.fieldKey?.[displayField as unknown as keyof T] as string),
            labelText: formData.labelText,
        };
        setListItems([...newItems, newItem]);
        setData([...newItems, newItem]);
    };

    const onUpdate = <T,>(formData: { fieldKey: T; labelText: FilterListDataItem['labelText'] }) => {
        const updatedFilters = listItems.map((item) => ({
            ...item,
            ...(currentItem === item.fieldKey
                ? {
                      fieldKey:
                          typeof formData.fieldKey === 'string'
                              ? formData.fieldKey
                              : (formData.fieldKey?.[valueField as unknown as keyof T] as string),
                      fieldName:
                          typeof formData.fieldKey === 'string'
                              ? formData.fieldKey
                              : (formData.fieldKey?.[displayField as unknown as keyof T] as string),
                      labelText: formData.labelText,
                  }
                : {}),
        }));
        setListItems(updatedFilters);
        setCurrentItem(null);
        setData(updatedFilters);
    };
    const onDragOver = (event: SortableOnDragOverEvent) => {
        setData(event.newState as FilterListDataItem[]);
        setListItems(event.newState as FilterListDataItem[]);
    };

    const onSubmit = <T,>(formData: { fieldKey: T | undefined; labelText: FilterListDataItem['labelText'] }) => {
        if (currentItem) {
            onUpdate(formData);
        } else {
            onAddNew(formData);
        }

        showAddEditDialog.setFalse();
    };

    const onClose = () => {
        showAddEditDialog.setFalse();
        setCurrentItem(null);
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setListItems(listItems.map((item) => ({ ...item, isEdit, isDisabled: !isEdit })));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isEdit]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (data) setListItems(modifyFilterListItems(data, onDelete, onEdit));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    return (
        <>
            {showAddEditDialog.value && (
                <AddEditFilterListItem
                    selectData={selectData}
                    displayField={displayField}
                    valueField={valueField}
                    reservedItems={listItems}
                    currentItem={currentItem ? listItems.find((item) => item.fieldKey === currentItem) : null}
                    onSubmit={onSubmit}
                    onClose={onClose}
                />
            )}
            {isEdit && <AddNewFilterListItem onAddNew={onDialogOpen} />}
            <Sortable
                itemUI={(props) => FilterListItem({ ...props, selectedItem: currentItem })}
                idField="fieldKey"
                disabledField="isDisabled"
                data={listItems}
                setData={setListItems}
                onDragOver={onDragOver}
            />
        </>
    );
}) as <T>(props: FilterListProps<T> & { ref?: React.ForwardedRef<FilterListDataHandle> }) => JSX.Element;

export default FilterList;
